import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Avatar, FormDialog } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const ROLE_OPTS = [{ value: "employee", label: "Employee" }, { value: "admin", label: "Admin" }];

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [team, setTeam] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/team").then((r) => setTeam(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    const body = { name: f.name, title: f.title, role: f.role, hourly_rate: Number(f.hourly_rate) || 0 };
    if (edit?.id) await api.put(`/team/${edit.id}`, body); else await api.post("/team", { ...f, hourly_rate: Number(f.hourly_rate) || 0 });
    toast.success(edit?.id ? "Member updated" : "Team member added"); load();
  };
  const remove = async (m) => { if (!window.confirm(`Remove ${m.name} from the team? Their tasks become unassigned.`)) return; try { await api.delete(`/team/${m.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const fields = [
    { name: "name", label: "Full name", required: true }, { name: "title", label: "Job title" },
    ...(edit?.id ? [] : [{ name: "email", label: "Email", type: "email", required: true }, { name: "password", label: "Temporary password", type: "password", required: true }]),
    { name: "role", label: "Role", type: "select", options: ROLE_OPTS }, { name: "hourly_rate", label: "Hourly cost rate (USD)", type: "number" },
  ];

  return (
    <div data-testid="team-page">
      <PageHeader eyebrow="People" title="Team" subtitle="Who's on the team and how loaded they are right now."
        actions={isAdmin && <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setEdit({ role: "employee" })} data-testid="add-member-btn"><Plus size={16} className="mr-1" />Add member</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {team.map((m, i) => (
          <div key={m.id} className="panel panel-hover p-5 fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`member-card-${m.id}`}>
            <div className="flex items-center gap-3">
              <Avatar name={m.name} src={m.picture} size={44} />
              <div className="flex-1 min-w-0"><div className="font-heading font-semibold truncate">{m.name}</div><div className="text-xs text-slate-500 truncate">{m.title || "Team member"} · <span className="uppercase tracking-wide">{m.role}</span></div></div>
              {isAdmin && <div className="flex gap-1"><button onClick={() => setEdit(m)} className="text-slate-400 hover:text-slate-900" data-testid={`edit-member-${m.id}`}><Pencil size={14} /></button>{m.id !== user.id && <button onClick={() => remove(m)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-member-${m.id}`}><Trash2 size={14} /></button>}</div>}
            </div>
            <div className="text-xs text-slate-500 mt-3 truncate">{m.email}{isAdmin && m.hourly_rate != null && <span className="num"> · ${m.hourly_rate}/h</span>}</div>
            <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-slate-100 text-center">
              <div><div className="num text-lg font-semibold">{m.open_tasks}</div><div className="text-[10px] uppercase tracking-wide text-slate-500">Open</div></div>
              <div><div className={`num text-lg font-semibold ${m.overdue_tasks ? "text-rose-600" : ""}`}>{m.overdue_tasks}</div><div className="text-[10px] uppercase tracking-wide text-slate-500">Overdue</div></div>
              <div><div className="num text-lg font-semibold text-emerald-600">{m.done_tasks}</div><div className="text-[10px] uppercase tracking-wide text-slate-500">Done</div></div>
              <div><div className="num text-lg font-semibold text-blue-600">{m.hours_this_month}h</div><div className="text-[10px] uppercase tracking-wide text-slate-500">This month</div></div>
            </div>
          </div>
        ))}
      </div>
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit member" : "Add team member"} fields={fields} initial={edit ? { hourly_rate: 50, ...edit } : {}} onSubmit={save} testId="member-dialog" />
    </div>
  );
}
